import type { Lang } from '../types';

export interface RegisterText {
  title: string;
  subtitle: string;
  billingCycleLabel: string;
  monthly: string;
  annual: string;
  annualBadge: string;
  apartmentsLabel: string;
  apartment: string;
  apartments: (n: number) => string;
  apartmentsPlus: string;
  annualSaving: (czk: number) => string;
  estimatedCost: (czk: number) => string;
  estimatedAnnual: (czk: number) => string;
  enterpriseTitle: string;
  enterpriseBody: string;
  enterpriseContact: string;
  enterpriseSub: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequired: string;
  phoneInvalid: string;
  icoLabel: string;
  icoHint: string;
  icoInvalid: string;
  agreeTerms: string;
  termsLink: string;
  termsRequired: string;
  agreeGdpr: string;
  gdprLink: string;
  gdprRequired: string;
  submitBtn: string;
  submittingBtn: string;
  successAlert: string;
  hasAccount: string;
  signIn: string;
  privacyLink: string;
  /**
   * Keyed by the backend's stable AuthErrorCode (see cz.vuva.guestbook.service.AuthErrorCode) —
   * never render err.response.data.message directly, it's a server-side-safe fallback only, not
   * localized text (issue #88). Only EMAIL_ALREADY_IN_USE/EMAIL_PENDING_VERIFICATION/
   * VALIDATION_ERROR/INTERNAL_ERROR are reachable today; the rest are defined so this map doesn't
   * need another change when real email/password/phone/IČO validation is added.
   */
  errorCodes: Record<string, string>;
}

const EN_ERROR_CODES: Record<string, string> = {
  EMAIL_ALREADY_IN_USE: 'This email is already registered.',
  EMAIL_PENDING_VERIFICATION: 'This email is awaiting verification.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  WEAK_PASSWORD: 'Please choose a stronger password.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  ICO_INVALID: 'Please enter a valid company ID (IČO).',
  RATE_LIMITED: 'Too many attempts. Please try again later.',
  VALIDATION_ERROR: 'Please check the information you entered.',
  INTERNAL_ERROR: 'Something went wrong. Please try again later.',
};

export const registerText: Record<Lang, RegisterText> = {
  cs: {
    title: 'Vytvořit účet správce',
    subtitle: 'Zaregistrujte se a začněte spravovat byty a registrace hostů.',
    billingCycleLabel: 'Fakturační cyklus',
    monthly: 'Měsíční',
    annual: 'Roční',
    annualBadge: '−25%',
    apartmentsLabel: 'Kolik bytů plánujete spravovat?',
    apartment: '1 byt',
    apartments: (n: number) => `${n} byty`,
    apartmentsPlus: '20+ bytů',
    annualSaving: czk => `Roční úspora: ~${czk.toLocaleString()} CZK/rok`,
    estimatedCost: czk => `Odhadem: ${czk.toLocaleString()} CZK / měsíc`,
    estimatedAnnual: czk => ` · ${czk.toLocaleString()} CZK/rok účtováno ročně`,
    enterpriseTitle: 'Spravujete 10+ bytů?',
    enterpriseBody: 'Máte nárok na individuální cenu — kontaktujte nás před registrací.',
    enterpriseContact: 'Kontaktujte nás',
    enterpriseSub: 'Nebo pokračujte níže a zaregistrujte se za standardní cenu.',
    firstNameLabel: 'Jméno',
    firstNamePlaceholder: 'Jan',
    lastNameLabel: 'Příjmení',
    lastNamePlaceholder: 'Novák',
    emailLabel: 'E-mailová adresa',
    emailPlaceholder: 'vy@priklad.cz',
    passwordLabel: 'Heslo',
    passwordPlaceholder: '••••••••',
    phoneLabel: 'Telefonní číslo',
    phonePlaceholder: '+420 777 123 456',
    phoneRequired: 'Telefonní číslo je povinné',
    phoneInvalid: 'Zadejte platné české číslo (např. +420 777 123 456)',
    icoLabel: 'IČO',
    icoHint: '(IČ společnosti, nepovinné)',
    icoInvalid: 'IČO musí mít přesně 8 číslic',
    agreeTerms: 'Souhlasím s',
    termsLink: 'Obchodními podmínkami',
    termsRequired: 'Souhlas s obchodními podmínkami je povinný',
    agreeGdpr: 'Souhlasím se',
    gdprLink: 'zpracováním osobních údajů (GDPR)',
    gdprRequired: 'Souhlas se zpracováním dat je povinný',
    submitBtn: 'Vytvořit účet',
    submittingBtn: 'Vytvářím…',
    successAlert: 'Účet správce byl úspěšně vytvořen! Přesměrování na přihlášení…',
    hasAccount: 'Již máte účet?',
    signIn: 'Přihlásit se',
    privacyLink: 'Nastavení soukromí',
    errorCodes: {
      EMAIL_ALREADY_IN_USE: 'Tento e-mail je již zaregistrován.',
      EMAIL_PENDING_VERIFICATION: 'Tento e-mail čeká na ověření.',
      INVALID_EMAIL: 'Zadejte platnou e-mailovou adresu.',
      WEAK_PASSWORD: 'Zvolte prosím silnější heslo.',
      INVALID_PHONE: 'Zadejte platné telefonní číslo.',
      ICO_INVALID: 'Zadejte platné IČO.',
      RATE_LIMITED: 'Příliš mnoho pokusů. Zkuste to prosím později.',
      VALIDATION_ERROR: 'Zkontrolujte prosím zadané údaje.',
      INTERNAL_ERROR: 'Něco se nepodařilo. Zkuste to prosím později.',
    },
  },
  en: {
    title: 'Create manager account',
    subtitle: 'Register to start managing apartments and guest registrations.',
    billingCycleLabel: 'Billing cycle',
    monthly: 'Monthly',
    annual: 'Annual',
    annualBadge: '−25%',
    apartmentsLabel: 'How many apartments are you planning to manage?',
    apartment: '1 apartment',
    apartments: (n: number) => `${n} apartments`,
    apartmentsPlus: '20+ apartments',
    annualSaving: czk => `Annual saving: ~${czk.toLocaleString()} CZK/year`,
    estimatedCost: czk => `Estimated: ${czk.toLocaleString()} CZK / month`,
    estimatedAnnual: czk => ` · ${czk.toLocaleString()} CZK/year billed annually`,
    enterpriseTitle: 'Managing 10+ apartments?',
    enterpriseBody: "You qualify for custom pricing — let's talk before you register.",
    enterpriseContact: 'Contact us for a deal',
    enterpriseSub: 'Or continue below to register at standard pricing.',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'Jan',
    lastNameLabel: 'Last name',
    lastNamePlaceholder: 'Novák',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@example.com',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    phoneLabel: 'Phone number',
    phonePlaceholder: '+420 777 123 456',
    phoneRequired: 'Phone number is required',
    phoneInvalid: 'Enter a valid Czech phone number (e.g. +420 777 123 456)',
    icoLabel: 'IČO',
    icoHint: '(company registration number, optional)',
    icoInvalid: 'IČO must be exactly 8 digits',
    agreeTerms: 'I agree to the',
    termsLink: 'Terms and Conditions',
    termsRequired: 'Agreement to terms is required',
    agreeGdpr: 'I agree to the',
    gdprLink: 'processing of personal data (GDPR)',
    gdprRequired: 'Agreement to data processing is required',
    submitBtn: 'Create account',
    submittingBtn: 'Creating account…',
    successAlert: 'Manager account created successfully! Redirecting to login…',
    hasAccount: 'Already have an account?',
    signIn: 'Sign in',
    privacyLink: 'Privacy settings',
    errorCodes: EN_ERROR_CODES,
  },
  vi: {
    title: 'Tạo tài khoản quản lý',
    subtitle: 'Đăng ký để bắt đầu quản lý căn hộ và đăng ký khách.',
    billingCycleLabel: 'Chu kỳ thanh toán',
    monthly: 'Hàng tháng',
    annual: 'Hàng năm',
    annualBadge: '−25%',
    apartmentsLabel: 'Bạn dự định quản lý bao nhiêu căn hộ?',
    apartment: '1 căn hộ',
    apartments: (n: number) => `${n} căn hộ`,
    apartmentsPlus: '20+ căn hộ',
    annualSaving: czk => `Tiết kiệm hàng năm: ~${czk.toLocaleString()} CZK/năm`,
    estimatedCost: czk => `Ước tính: ${czk.toLocaleString()} CZK / tháng`,
    estimatedAnnual: czk => ` · ${czk.toLocaleString()} CZK/năm tính theo năm`,
    enterpriseTitle: 'Quản lý 10+ căn hộ?',
    enterpriseBody: 'Bạn đủ điều kiện nhận giá đặc biệt — hãy liên hệ trước khi đăng ký.',
    enterpriseContact: 'Liên hệ để được ưu đãi',
    enterpriseSub: 'Hoặc tiếp tục đăng ký với giá tiêu chuẩn.',
    firstNameLabel: 'Tên',
    firstNamePlaceholder: 'Nguyen',
    lastNameLabel: 'Họ',
    lastNamePlaceholder: 'Van A',
    emailLabel: 'Địa chỉ email',
    emailPlaceholder: 'ban@example.com',
    passwordLabel: 'Mật khẩu',
    passwordPlaceholder: '••••••••',
    phoneLabel: 'Số điện thoại',
    phonePlaceholder: '+420 777 123 456',
    phoneRequired: 'Số điện thoại là bắt buộc',
    phoneInvalid: 'Nhập số điện thoại Czech hợp lệ (vd. +420 777 123 456)',
    icoLabel: 'IČO',
    icoHint: '(mã số công ty, không bắt buộc)',
    icoInvalid: 'IČO phải có đúng 8 chữ số',
    agreeTerms: 'Tôi đồng ý với',
    termsLink: 'Điều khoản dịch vụ',
    termsRequired: 'Bắt buộc đồng ý với điều khoản',
    agreeGdpr: 'Tôi đồng ý với',
    gdprLink: 'xử lý dữ liệu cá nhân (GDPR)',
    gdprRequired: 'Bắt buộc đồng ý với xử lý dữ liệu',
    submitBtn: 'Tạo tài khoản',
    submittingBtn: 'Đang tạo…',
    successAlert: 'Tài khoản quản lý đã được tạo! Đang chuyển hướng đến trang đăng nhập…',
    hasAccount: 'Đã có tài khoản?',
    signIn: 'Đăng nhập',
    privacyLink: 'Cài đặt quyền riêng tư',
    // Issue #88 only required a CZ+EN map; Vietnamese/Ukrainian fall back to English here.
    errorCodes: EN_ERROR_CODES,
  },
  uk: {
    title: 'Створити обліковий запис',
    subtitle: 'Зареєструйтесь, щоб почати керувати квартирами та реєстрацією гостей.',
    billingCycleLabel: 'Цикл виставлення рахунків',
    monthly: 'Щомісячно',
    annual: 'Щорічно',
    annualBadge: '−25%',
    apartmentsLabel: 'Скількома квартирами ви плануєте керувати?',
    apartment: '1 квартира',
    apartments: (n: number) => `${n} квартири`,
    apartmentsPlus: '20+ квартир',
    annualSaving: czk => `Річна економія: ~${czk.toLocaleString()} CZK/рік`,
    estimatedCost: czk => `Орієнтовно: ${czk.toLocaleString()} CZK / місяць`,
    estimatedAnnual: czk => ` · ${czk.toLocaleString()} CZK/рік при річній оплаті`,
    enterpriseTitle: 'Керуєте 10+ квартирами?',
    enterpriseBody: 'Ви маєте право на індивідуальну ціну — зв\'яжіться з нами перед реєстрацією.',
    enterpriseContact: 'Зв\'язатися для отримання пропозиції',
    enterpriseSub: 'Або продовжте реєстрацію за стандартною ціною.',
    firstNameLabel: "Ім'я",
    firstNamePlaceholder: 'Іван',
    lastNameLabel: 'Прізвище',
    lastNamePlaceholder: 'Петренко',
    emailLabel: 'Електронна адреса',
    emailPlaceholder: 'ви@example.com',
    passwordLabel: 'Пароль',
    passwordPlaceholder: '••••••••',
    phoneLabel: 'Номер телефону',
    phonePlaceholder: '+420 777 123 456',
    phoneRequired: 'Номер телефону є обов\'язковим',
    phoneInvalid: 'Введіть дійсний чеський номер (напр. +420 777 123 456)',
    icoLabel: 'IČO',
    icoHint: '(реєстраційний номер компанії, необов\'язково)',
    icoInvalid: 'IČO має містити рівно 8 цифр',
    agreeTerms: 'Я погоджуюсь з',
    termsLink: 'Умовами надання послуг',
    termsRequired: 'Погодження з умовами є обов\'язковим',
    agreeGdpr: 'Я погоджуюсь на',
    gdprLink: 'обробку персональних даних (GDPR)',
    gdprRequired: 'Погодження на обробку даних є обов\'язковим',
    submitBtn: 'Створити обліковий запис',
    submittingBtn: 'Створюю…',
    successAlert: 'Обліковий запис успішно створено! Перенаправлення на сторінку входу…',
    hasAccount: 'Вже є обліковий запис?',
    signIn: 'Увійти',
    privacyLink: 'Налаштування конфіденційності',
    errorCodes: EN_ERROR_CODES,
  },
};
